import { useState, useEffect } from 'react'
import { Plus, Search, X, ArrowDownCircle, ArrowUpCircle, RefreshCw } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import { Articolo } from '../../../types/magazzino'
import { Movimento, MovimentoForm, TIPI_MOVIMENTO, TIPI_MOVIMENTO_LABELS, TipoMovimento } from '../../../types/magazzino'

const emptyForm: MovimentoForm = {
  articolo_id: '',
  tipo: 'carico',
  quantita: '',
  causale: '',
  note: '',
}

const TIPO_BADGE: Record<TipoMovimento, { bg: string; icon: typeof ArrowDownCircle }> = {
  carico: { bg: 'bg-green-100 text-green-700', icon: ArrowDownCircle },
  scarico: { bg: 'bg-red-100 text-red-700', icon: ArrowUpCircle },
  rettifica: { bg: 'bg-blue-100 text-blue-700', icon: RefreshCw },
}

export default function MovimentiPage() {
  const [movimenti, setMovimenti] = useState<Movimento[]>([])
  const [articoli, setArticoli] = useState<Articolo[]>([])
  const [loading, setLoading] = useState(true)
  const [filterArticolo, setFilterArticolo] = useState('')
  const [filterTipo, setFilterTipo] = useState('')
  const [searchArticolo, setSearchArticolo] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<MovimentoForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [movRes, artRes] = await Promise.all([
        api.get('/v1/magazzino/movimenti'),
        api.get('/v1/magazzino/articoli'),
      ])
      setMovimenti(movRes.data)
      setArticoli(artRes.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const filteredMovimenti = movimenti.filter(m => {
    const matchArticolo = !filterArticolo || m.articolo_id === filterArticolo
    const matchTipo = !filterTipo || m.tipo === filterTipo
    return matchArticolo && matchTipo
  })

  const handleCreate = () => {
    setForm(emptyForm)
    setError('')
    setSearchArticolo('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.articolo_id) {
      setError('Seleziona un articolo')
      return
    }
    if (!form.quantita || parseFloat(form.quantita) <= 0) {
      setError('Inserisci una quantita valida')
      return
    }

    setSaving(true)
    try {
      const data = {
        articolo_id: form.articolo_id,
        tipo: form.tipo,
        quantita: parseFloat(form.quantita),
        causale: form.causale.trim() || null,
        note: form.note.trim() || null,
      }
      await api.post('/v1/magazzino/movimenti', data)

      const selectedArt = articoli.find(a => a.id === form.articolo_id)
      setToast({ message: `Movimento ${TIPI_MOVIMENTO_LABELS[form.tipo as TipoMovimento]} registrato per ${selectedArt?.codice || 'articolo'}`, type: 'success' })
      setShowModal(false)
      loadData()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const filteredArticoliDropdown = articoli.filter(a =>
    a.attivo && (!searchArticolo ||
      a.codice.toLowerCase().includes(searchArticolo.toLowerCase()) ||
      a.descrizione.toLowerCase().includes(searchArticolo.toLowerCase()))
  )

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Movimenti Magazzino</h1>
          <p className="text-gray-500">Registro carichi, scarichi e rettifiche</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Nuovo Movimento
        </button>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[200px]">
            <select
              value={filterArticolo}
              onChange={(e) => setFilterArticolo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">Tutti gli articoli</option>
              {articoli.map(a => (
                <option key={a.id} value={a.id}>{a.codice} - {a.descrizione}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterTipo('')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                !filterTipo ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tutti
            </button>
            {TIPI_MOVIMENTO.map(tipo => (
              <button
                key={tipo}
                onClick={() => setFilterTipo(tipo === filterTipo ? '' : tipo)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterTipo === tipo ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {TIPI_MOVIMENTO_LABELS[tipo]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : filteredMovimenti.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nessun movimento registrato</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Ora</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Articolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantita'</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Causale</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMovimenti.map(m => {
                const badge = TIPO_BADGE[m.tipo]
                const Icon = badge.icon
                return (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(m.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="font-mono text-sm font-medium text-gray-900">{m.articolo_codice}</div>
                      <div className="text-xs text-gray-500">{m.articolo_descrizione}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}>
                        <Icon className="w-3 h-3" />
                        {TIPI_MOVIMENTO_LABELS[m.tipo]}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">{m.quantita}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{m.causale || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">{m.note || '-'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Nuovo Movimento */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Nuovo Movimento</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              {/* Seleziona Articolo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Articolo *</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca articolo..."
                    value={searchArticolo}
                    onChange={(e) => setSearchArticolo(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </div>
                {form.articolo_id && (
                  <div className="mt-1 text-sm text-primary-600 font-medium">
                    Selezionato: {articoli.find(a => a.id === form.articolo_id)?.codice} - {articoli.find(a => a.id === form.articolo_id)?.descrizione}
                  </div>
                )}
                <div className="mt-1 max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                  {filteredArticoliDropdown.slice(0, 20).map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => { setForm({ ...form, articolo_id: a.id }); setSearchArticolo('') }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                        form.articolo_id === a.id ? 'bg-primary-50 text-primary-700' : ''
                      }`}
                    >
                      <span className="font-mono font-medium">{a.codice}</span> — {a.descrizione}
                      <span className="text-gray-400 ml-2">(giac: {a.giacenza_attuale} {a.unita_misura})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo Movimento *</label>
                <div className="flex gap-3">
                  {TIPI_MOVIMENTO.map(tipo => {
                    const badge = TIPO_BADGE[tipo]
                    const Icon = badge.icon
                    return (
                      <label key={tipo} className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-colors ${
                        form.tipo === tipo ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="tipo"
                          value={tipo}
                          checked={form.tipo === tipo}
                          onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                          className="sr-only"
                        />
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{TIPI_MOVIMENTO_LABELS[tipo]}</span>
                      </label>
                    )
                  })}
                </div>
                {form.tipo === 'rettifica' && (
                  <p className="mt-1 text-xs text-blue-600">La rettifica imposta il nuovo valore assoluto della giacenza</p>
                )}
              </div>

              {/* Quantita */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.tipo === 'rettifica' ? 'Nuova Giacenza *' : 'Quantita *'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={form.quantita}
                  onChange={(e) => setForm({ ...form, quantita: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={form.tipo === 'rettifica' ? 'Nuovo valore giacenza' : 'Quantita'}
                />
              </div>

              {/* Causale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Causale</label>
                <input
                  type="text"
                  value={form.causale}
                  onChange={(e) => setForm({ ...form, causale: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Motivo del movimento"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                  Annulla
                </button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {saving ? 'Registrazione...' : 'Registra Movimento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
